import { useCallback, useEffect, useState } from 'react';
import { STRINGS } from '@/strings/strings';
import { ApiError } from '@/lib/api-client';
import { regenCover } from '../../api';
import type { CustomerRenderState, PreviewCoverRegen, PreviewPage } from '../../types';
import { useSceneTextEdit } from '../../use-scene-text-edit';
import { CharacterCounter } from '../geschichte/CharacterCounter';
import {
  COVER_REGEN_COMMENT_MAX_LENGTH as COVER_COMMENT_MAX_CHARS,
  COVER_REGEN_TITLE_MAX_LENGTH as COVER_TITLE_MAX_CHARS,
} from '../../constants/text-limits';
import { AttemptPickerDialog } from './AttemptPickerDialog';
import { PanelMoveOverlay } from './PanelMoveOverlay';
import { PanelAndTextEditor } from './PanelAndTextEditor';
import { RegenImageOverlay } from './RegenImageOverlay';

/**
 * PageChangeDialog — the Freigabe screen's per-slot Change dialog
 * (Position-1 Commit-2, Sebastian rulings 2026-09-03 + 2026-09-04 +
 * cover-regen slice 2026-09-05).
 *
 * Two views on PAGE kind:
 *   - 'menu'   → three-option surface (heading + subheading + three
 *                buttons). Wording released verbatim; DO NOT alter.
 *                Cover slots also disable option 2 (there is no
 *                panel to move on a cover).
 *   - 'editor' → inline text editor (textarea + character counter
 *                + Save / Abbrechen).
 *
 * COVER KIND is one option, not three (Sebastian ruling 2026-09-05):
 *   - The menu on cover kind renders ONLY option 1 (a two-field
 *     editor: title + comment). Options 2 and 3 are absent from the
 *     cover menu — deliberate, not disabled.
 *   - Option 1 for cover opens the two-field editor. The Save button
 *     is disabled when (a) `renderState === 'rendering'` (cover
 *     render in flight — same lock as scene option 3), (b)
 *     `regen.remaining === 0` (cap reached), OR (c) both fields are
 *     empty after trim (both-empty refuses; the endpoint enforces
 *     the same rule server-side with `cover_regen_nothing_to_change`).
 *   - On Save the endpoint `POST /api/book/orders/:orderId/cover/
 *     regen` fires; cap counter counts EVERY attempt with regenCause
 *     'customer_cover_regen', accepted or not.
 *
 * Slot-awareness:
 *   - Page: option-1 label reads "1. Den Szenentext"; opens the
 *           editor with `page.text` prefilled + `page.maxCharsForPanel`
 *           as the counter's max (falls back to 150 when null per
 *           Sebastian's null-behaviour ruling; corridor's outer 200
 *           cap stays as a hard-visible ceiling for defense in depth).
 *   - Cover: option-1 label reads "1. Den Buchtitel oder das Bild";
 *            opens the two-field editor described above.
 *
 * Save contract: on success the parent refetches the envelope via
 * `onSaved` (page) / `onRegenSaved` (page-regen) / `onSaved` (cover).
 * The server has already recomposed the one page for scene-text
 * edits; cover regens fire a background render and rely on the
 * parent's envelope poll to surface the new image.
 */

/** Fallback cap the client shows when `maxCharsForPanel` is null.
 *  Mirrors the server-side `PANEL_CAPACITY_UNMEASURABLE_CAP` at
 *  `src/book/panel-detection/panel-capacity.ts`. Server is authoritative
 *  — if the two ever drift, server wins and the customer sees the
 *  server's `scene_text_too_long` on save.
 *  Same constant is used server-side for BOTH the null-panelBox
 *  branch AND the zone-fallback branch (Sebastian ruling 2026-09-04,
 *  "capacity not measurable" reasoning). Two branches, one number,
 *  one reason. */
const CLIENT_NULL_CAP_FALLBACK = 150;

// Resizable-panel slice (Sebastian ruling 2026-09-17). Outer counter
// bound is delivered by the envelope via
// `panelGeometry.maxCharsScene`. 300 today for customer-resized
// pages; server enforces the same value. If the two ever drift the
// server wins and the customer sees a `scene_text_too_long` on save.
// (The pre-slice `CORRIDOR_OUTER_CAP = 200` literal was replaced by
// this envelope read.)

/** Customer-edit floor for the finished book — Sebastian ruling
 *  2026-09-03 late-evening. Mirrors
 *  `BOOK_CONFIG.scene_text_min_chars_customer_review` on the server.
 *  10 is the "not empty by accident" floor, well below the generator's
 *  60-char discipline. Used as the counter's `min` so the customer
 *  sees the counter turn green as soon as the save will succeed
 *  (min ≤ length ≤ max). Server is authoritative; if this drifts the
 *  customer sees the server's `scene_text_too_short` message on save
 *  via the envelope render path added in this slice. */
const CUSTOMER_EDIT_MIN_CHARS = 10;

export interface PageChangeDialogPageProps {
  readonly kind: 'page';
  readonly bookOrderId: string;
  readonly sceneId: string;
  readonly initialText: string;
  readonly maxCharsForPanel: number | null;
  /**
   * Position-1 Commit-3 addendum — composer-derived drawn geometry
   * for the drag surface outline. `null` when the composer would
   * fall back to zone OR the picker has no panelBox. Both cases
   * suppress option 2 by construction.
   */
  readonly drawnPanel: PreviewPage['drawnPanel'];
  /**
   * Position-1 Commit-3 — signed URL for the raw scene image (no
   * panel overlay). Drag surface background. `null` also suppresses
   * option 2.
   */
  readonly rawImageUrl: string | null;
  /**
   * Resizable-panel slice (Sebastian ruling 2026-09-17). Doc-fraction
   * geometry bundle threaded from the envelope. Consumed by
   * `PanelMoveOverlay` for the margin cage + resize clamps + the
   * customer 300-char ceiling. Present on every envelope from the
   * fix-2 backend onward; fix-4 (deploy-order safety) makes
   * consumers tolerate a missing value.
   */
  readonly panelGeometry?: import('../../types').PreviewPanelGeometry;
  /**
   * Position-1 Commit-4 (Sebastian ruling 2026-09-04) — current
   * envelope render-state for THIS page. When 'rendering', option
   * 3 is disabled with an inline note ("Warte kurz, dein Bild wird
   * noch gezeichnet.") — second regen while a first is still
   * running would enqueue redundantly and burn cap without waiting
   * for the first result.
   */
  readonly renderState: PreviewPage['renderState'];
  /**
   * Position-1 Commit-4 — remaining per-page regen allowance,
   * NULL until the customer has used at least one (Sebastian
   * ruling: nothing shown before first use). When 0, option 3
   * is disabled and the customer sees the catalog's
   * `scene_regen_page_cap_reached` message on the next server
   * attempt. When 1 or 2 the overlay shows the ability-framed
   * remaining line under option 3.
   */
  readonly regenRemainingPage: number | null;
  /**
   * Pick-earlier-attempt slice (Sebastian ruling 2026-09-06) — from
   * envelope `PreviewPage.usedPage`. Menu-entry visibility gate for
   * "Ein früheres Bild wählen": `usedPage > 0` iff more than one
   * image exists for this scene. Post QA-removal every customer_regen
   * attempt reaches accepted, so `used > 0` ↔ >1 image exactly.
   */
  readonly usedPage: number;
  readonly onClose: () => void;
  readonly onSaved: () => void;
  /**
   * Position-1 Commit-4 revisit (Sebastian ruling 2026-09-04 late-
   * evening) — distinct callback for a successful regen so the parent
   * ReviewScreen can arm the envelope poll at trigger time. Text-edit
   * + panel-move do not kick off a background render; using onSaved
   * for the regen path would arm the poll for those too, and the poll
   * would run forever (envelope never sees rendering, `pollActive`
   * never clears). Text-edit + panel-move continue to call `onSaved`
   * only.
   */
  readonly onRegenSaved: () => void;
}

export interface PageChangeDialogCoverProps {
  readonly kind: 'cover';
  readonly bookOrderId: string;
  readonly initialTitle: string;
  readonly onClose: () => void;
  /**
   * Cover-regen slice (Sebastian ruling 2026-09-05) — fired after a
   * successful cover regen. The parent ReviewScreen uses it to arm
   * the envelope poll so the cover thumbnail cycles to the
   * "rendering" state and then to the fresh anchor image once the
   * render lands. Same shape as the page-path `onRegenSaved`.
   */
  readonly onSaved: () => void;
  /**
   * Cover-regen slice (Sebastian ruling 2026-09-05) — cover render
   * state (`'rendering' | 'ready' | 'retry_available' | 'blocked'`)
   * from `PreviewCover.renderState`. The two-field editor's Save
   * button is disabled when this is `'rendering'` (Sebastian ruling:
   * "Cover dialog inherits the same lock").
   */
  readonly renderState: import('../../types').CustomerRenderState;
  /**
   * Cover-regen slice (Sebastian ruling 2026-09-05) — nested regen
   * counter object from `PreviewCover.regen`. Carries cap + used +
   * remaining (+ empty attempts placeholder for the pick-earlier
   * slice). Save button gate: also disabled when
   * `regen.remaining === 0`. Menu-entry visibility gate (pick-earlier
   * slice, Sebastian ruling 2026-09-06): `regen.used > 0` iff more
   * than one cover image exists.
   */
  readonly regen: import('../../types').PreviewCoverRegen;
}

export type PageChangeDialogProps = PageChangeDialogPageProps | PageChangeDialogCoverProps;

export function PageChangeDialog(props: PageChangeDialogProps) {
  // Sebastian ruling 2026-09-17 fix-5 — two-option chooser on the
  // page kind, plus a new 'combined-edit' view that folds text edit
  // + panel move/resize into one round-trip. Pre-fix-5 views:
  // 'move' + 'editor' were separate; retained ONLY as fallback
  // paths when the new combined endpoint is unavailable.
  const [view, setView] = useState<
    'menu' | 'combined-edit' | 'editor' | 'move' | 'regen' | 'attempt-pick'
  >('menu');
  const [combinedEndpointGave404, setCombinedEndpointGave404] = useState(false);
  const [
    combinedFallbackSeed,
    setCombinedFallbackSeed,
  ] = useState<{ text: string; width: number; left: number; top: number } | null>(null);

  // Escape closes the dialog. Menu → close; editor → close (no
  // multi-step back). Sebastian ruled dialog is a single-level modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') props.onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [props]);

  const option1Label =
    props.kind === 'cover'
      ? STRINGS.book.review.changeOption1Cover
      : STRINGS.book.review.changeOption1Page;

  // Sebastian ruling 2026-09-17 fix-5 (item A). Two-option chooser
  // on the page kind:
  //   Option 1 → combined text + panel editor (when
  //              drawnPanel + rawImageUrl available AND combined
  //              endpoint has not 404'd this session); else falls
  //              back to the pre-fix-5 text-only editor.
  //   Option 2 → scene regen (was option 3 pre-fix-5).
  // Cover kind unchanged — option 1 opens the cover regen editor;
  // option 2 does not exist on cover.
  const handleOption1 = useCallback(() => {
    if (props.kind !== 'page') {
      setView('editor');
      return;
    }
    const combinedAvailable =
      !combinedEndpointGave404 &&
      props.drawnPanel !== null &&
      props.rawImageUrl !== null;
    setView(combinedAvailable ? 'combined-edit' : 'editor');
  }, [props, combinedEndpointGave404]);

  // Option 1 always enabled for cover. For page: enabled when the
  // combined editor is reachable OR when the pre-fix-5 text-only
  // editor is reachable (which needs no drawnPanel). Text-only
  // editor works on any page; keep option 1 always enabled on
  // page kind.
  const option1Enabled = true;

  // Fix-5 option 2 = scene regen (was option 3 pre-fix-5). Same
  // gates as pre-fix-5 option 3: page slot only, NOT currently
  // rendering, NOT at 0-remaining.
  const isRegenPending = props.kind === 'page' && props.renderState === 'rendering';
  const option2Enabled =
    props.kind === 'page' &&
    !isRegenPending &&
    (props.regenRemainingPage === null || props.regenRemainingPage > 0);

  const handleOption2 = useCallback(() => {
    if (props.kind !== 'page') return;
    setView('regen');
  }, [props]);

  // Pick-earlier-attempt slice (Sebastian ruling 2026-09-06) — menu-
  // entry visibility gate. Symmetric between kinds via the `used`
  // signal (page: envelope `usedPage`, cover: `regen.used`). Post
  // QA-removal (Sebastian ruling 2026-09-06) every customer_regen
  // reaches accepted, so `used > 0` means "more than one image
  // exists for this slot" exactly — not "at least one regen was
  // attempted". `remainingPage` / `remaining` intentionally NOT used
  // for gating: a customer at the cap has three images and must
  // still be able to pick.
  const attemptPickEnabled = props.kind === 'cover' ? props.regen.used > 0 : props.usedPage > 0;
  const handleAttemptPick = useCallback(() => {
    setView('attempt-pick');
  }, []);

  // Position-1 Commit-4 — per-page remaining copy under option 3.
  // Only shown after the customer has used at least one regen
  // (Sebastian ruling: nothing shown before). NULL means "not yet
  // used one" → no copy; 2 or 1 → ability-framed line; 0 → the
  // catalog's `scene_regen_page_cap_reached` handles at PATCH time.
  const remainingPageCopy =
    props.kind === 'page' && props.regenRemainingPage === 2
      ? STRINGS.book.review.regenRemainingPageTwo
      : props.kind === 'page' && props.regenRemainingPage === 1
        ? STRINGS.book.review.regenRemainingPageOne
        : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="page-change-dialog-heading"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={(e) => {
        // Click on backdrop closes. Click inside modal body does not.
        if (e.target === e.currentTarget) props.onClose();
      }}
    >
      <div className="builder-field border-dream-navy w-full max-w-md bg-white p-6 relative">
        {view === 'menu' && (
          <div className="space-y-4">
            <h2
              id="page-change-dialog-heading"
              className="text-lg font-semibold text-dream-navy pr-6"
            >
              {STRINGS.book.review.changeDialogHeading}
            </h2>
            <div className="flex flex-col gap-2">
              {/* Sebastian ruling 2026-09-17 fix-5 (item A) — two
                  options on the page kind, one on the cover kind.
                  No subtitle. Option 1 opens the combined text +
                  panel editor (page) or the cover regen editor
                  (cover). Option 2 opens the scene regen overlay
                  (page only). */}
              <button
                type="button"
                onClick={handleOption1}
                disabled={!option1Enabled}
                aria-disabled={!option1Enabled}
                className="text-left h-12 rounded-lg border border-dream-navy/30 px-4 text-base font-semibold text-dream-navy hover:bg-dream-navy/5"
              >
                {option1Label}
              </button>
              {props.kind === 'page' && (
                <>
                  <button
                    type="button"
                    onClick={handleOption2}
                    disabled={!option2Enabled}
                    aria-disabled={!option2Enabled}
                    className={
                      option2Enabled
                        ? 'text-left h-12 rounded-lg border border-dream-navy/30 px-4 text-base font-semibold text-dream-navy hover:bg-dream-navy/5'
                        : 'text-left h-12 rounded-lg border border-dream-navy/30 px-4 text-base text-dream-navy/40 cursor-not-allowed'
                    }
                  >
                    {STRINGS.book.review.changeOption2}
                  </button>
                  {isRegenPending && (
                    <p className="text-xs text-dream-navy/70 pt-1">
                      {STRINGS.book.review.regenPendingNote}
                    </p>
                  )}
                  {!isRegenPending && remainingPageCopy !== null && (
                    <p className="text-xs text-dream-navy/70 pt-1">{remainingPageCopy}</p>
                  )}
                </>
              )}
              {/* Pick-earlier-attempt slice (Sebastian ruling
                  2026-09-06) — "Ein früheres Bild wählen" appears
                  ONLY when more than one image exists for the slot.
                  Not automatic: whoever is happy should never think
                  in attempts. Same label on cover and page. */}
              {attemptPickEnabled && (
                <button
                  type="button"
                  onClick={handleAttemptPick}
                  className="text-left h-12 rounded-lg border border-dream-navy/30 px-4 text-base font-semibold text-dream-navy hover:bg-dream-navy/5"
                >
                  {STRINGS.book.review.attemptPickMenuOption}
                </button>
              )}
            </div>
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={props.onClose}
                className="h-10 rounded-lg border border-dream-navy/30 px-4 text-sm font-semibold text-dream-navy hover:bg-dream-navy/5"
              >
                {STRINGS.book.review.editorCancelButton}
              </button>
            </div>
          </div>
        )}

        {/* Sebastian ruling 2026-09-17 fix-5 — new combined editor
            (item B). Opened by option 1 on page kind when the
            envelope's `drawnPanel` + `rawImageUrl` are both present
            AND the combined save endpoint has not 404'd this
            session. On combined-endpoint 404 the editor calls
            `onCombinedEndpoint404` with the customer's in-flight
            values, we flip to 'editor' (pre-fix-5 text-only), and
            the parent's regular save falls back to the separate
            move + resize + text endpoints implicitly. */}
        {view === 'combined-edit' &&
          props.kind === 'page' &&
          props.drawnPanel !== null &&
          props.rawImageUrl !== null && (
            <PanelAndTextEditor
              bookOrderId={props.bookOrderId}
              sceneId={props.sceneId}
              initialText={props.initialText}
              initialDrawnPanel={props.drawnPanel}
              rawImageUrl={props.rawImageUrl}
              {...(props.panelGeometry !== undefined
                ? { panelGeometry: props.panelGeometry }
                : {})}
              onClose={props.onClose}
              onSaved={props.onSaved}
              onCombinedEndpoint404={(text, w, l, t) => {
                setCombinedEndpointGave404(true);
                setCombinedFallbackSeed({ text, width: w, left: l, top: t });
                setView('editor');
              }}
            />
          )}

        {view === 'editor' && props.kind === 'page' && (
          <PageTextEditor
            bookOrderId={props.bookOrderId}
            sceneId={props.sceneId}
            initialText={combinedFallbackSeed?.text ?? props.initialText}
            maxCharsForPanel={props.maxCharsForPanel}
            outerCharCeiling={props.panelGeometry?.maxCharsScene ?? 200}
            onClose={props.onClose}
            onSaved={props.onSaved}
          />
        )}

        {view === 'editor' && props.kind === 'cover' && (
          <CoverRegenEditor
            bookOrderId={props.bookOrderId}
            initialTitle={props.initialTitle}
            renderState={props.renderState}
            regen={props.regen}
            onClose={props.onClose}
            onSaved={props.onSaved}
          />
        )}

        {view === 'move' &&
          props.kind === 'page' &&
          props.drawnPanel !== null &&
          props.rawImageUrl !== null && (
            <PanelMoveOverlay
              bookOrderId={props.bookOrderId}
              sceneId={props.sceneId}
              initialDrawnPanel={props.drawnPanel}
              sceneText={props.initialText}
              rawImageUrl={props.rawImageUrl}
              {...(props.panelGeometry !== undefined
                ? { panelGeometry: props.panelGeometry }
                : {})}
              onClose={props.onClose}
              onSaved={props.onSaved}
            />
          )}

        {view === 'regen' && props.kind === 'page' && (
          <RegenImageOverlay
            bookOrderId={props.bookOrderId}
            sceneId={props.sceneId}
            onClose={props.onClose}
            onSaved={props.onRegenSaved}
          />
        )}

        {/* Pick-earlier-attempt slice (Sebastian ruling 2026-09-06) —
            same component for scene + cover kinds. The picker's own
            onPicked triggers the parent envelope refetch (via the
            slot's onSaved for cover, onRegenSaved for page — matches
            existing cover-regen / page-regen wiring). */}
        {view === 'attempt-pick' && props.kind === 'page' && (
          <AttemptPickerDialog
            kind="scene"
            bookOrderId={props.bookOrderId}
            sceneId={props.sceneId}
            onPicked={() => {
              props.onRegenSaved();
              props.onClose();
            }}
            onClose={props.onClose}
          />
        )}
        {view === 'attempt-pick' && props.kind === 'cover' && (
          <AttemptPickerDialog
            kind="cover"
            bookOrderId={props.bookOrderId}
            onPicked={() => {
              props.onSaved();
              props.onClose();
            }}
            onClose={props.onClose}
          />
        )}
      </div>
    </div>
  );
}

interface PageTextEditorProps {
  readonly bookOrderId: string;
  readonly sceneId: string;
  readonly initialText: string;
  readonly maxCharsForPanel: number | null;
  /**
   * Resizable-panel slice (Sebastian ruling 2026-09-17). Outer
   * counter ceiling from `envelope.panelGeometry.maxCharsScene`.
   * Replaces the pre-slice hardcoded 200 literal.
   */
  readonly outerCharCeiling: number;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}

function PageTextEditor({
  bookOrderId,
  sceneId,
  initialText,
  maxCharsForPanel,
  outerCharCeiling,
  onClose,
  onSaved,
}: PageTextEditorProps) {
  const [text, setText] = useState(initialText);
  // Position-1 Commit-3 addendum (Sebastian ruling 2026-09-03 late-
  // evening) — hold the server-supplied `user_message` when the save
  // fails, not just a boolean. Third site tonight where the backend
  // sends correct German and the client used to throw it away
  // (blank screen on foreign order, summary-review generationFailed,
  // and this dialog). Fold-in tracker: `[pwa-book-route-discards-
  // server-supplied-recovery-path]` names the whole pattern; this
  // slice fixes it for the dialog. When no envelope arrives (network
  // failure, no ApiError), fall back to `editorSaveError`.
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const edit = useSceneTextEdit(bookOrderId);

  // Resizable-panel slice — SECOND CORRECTION (Sebastian ruling
  // 2026-09-17 fix-2). Every text edit on the approval screen
  // accepts up to `outerCharCeiling` characters (envelope's
  // `panelGeometry.maxCharsScene`, 300 today), regardless of the
  // per-page `maxCharsForPanel` value. The pre-fix-2 min-with-
  // detected-cap logic tied the client counter to detected panel
  // geometry — retired here so the counter always reads "N / 300".
  // `maxCharsForPanel` stays on the wire (informational) but no
  // longer bounds the counter.
  void maxCharsForPanel;
  void CLIENT_NULL_CAP_FALLBACK;

  const handleSave = useCallback(async () => {
    setSaveErrorMessage(null);
    const result = await edit.save(sceneId, text);
    if (result.ok) {
      onSaved();
      onClose();
    } else {
      // Prefer the server's DreamableError envelope's `user_message`
      // (e.g. scene_text_too_short → "Dein Text ist zu kurz für diese
      // Seite. Bitte kürze ihn etwas." — the catalog entry with the
      // right message for the right error). Fall back to the generic
      // string only when nothing is on the wire (network failure).
      const envelopeMessage = result.error?.envelope?.user_message ?? null;
      setSaveErrorMessage(envelopeMessage ?? STRINGS.book.review.editorSaveError);
    }
  }, [edit, sceneId, text, onSaved, onClose]);

  const editorAriaLabel = STRINGS.book.review.editorAriaLabel.replace('{n}', sceneId);

  return (
    <div className="space-y-3">
      <h2 id="page-change-dialog-heading" className="text-lg font-semibold text-dream-navy pr-6">
        {STRINGS.book.review.changeDialogHeading}
      </h2>
      <textarea
        className="builder-field w-full px-3 py-2 text-base text-dream-navy outline-none resize-none"
        rows={6}
        value={text}
        // Resizable-panel slice — SECOND CORRECTION (Sebastian ruling
        // 2026-09-17 fix-2). Hard input stop at the envelope's
        // `panelGeometry.maxCharsScene` ceiling (300 today). Prevents
        // the customer from typing more than the corridor accepts;
        // the counter reads "N / 300" up to that number and stops.
        maxLength={outerCharCeiling}
        onChange={(e) => {
          setText(e.target.value);
          if (saveErrorMessage !== null) setSaveErrorMessage(null);
        }}
        aria-label={editorAriaLabel}
        autoFocus
      />
      {/* Counter is ALWAYS shown now (Sebastian ruling 2026-09-17 fix-2):
          "editor shows a live character counter while typing, in the
          form '123 / 300', reading the 300 from the envelope". Pre-
          fix-2 branch that hid the counter when maxCharsForPanel was
          null (server-side unmeasurable fallback) is retired — every
          page carries the same 300 ceiling. */}
      <CharacterCounter
        value={text.length}
        corridor={{ min: CUSTOMER_EDIT_MIN_CHARS, max: outerCharCeiling }}
      />
      {saveErrorMessage !== null && (
        <p role="alert" className="text-sm text-error">
          {saveErrorMessage}
        </p>
      )}
      <div className="flex gap-3 justify-end pt-1">
        <button
          type="button"
          onClick={onClose}
          disabled={edit.saving}
          className="h-10 rounded-lg border border-dream-navy/30 px-4 text-sm font-semibold text-dream-navy hover:bg-dream-navy/5 disabled:opacity-40"
        >
          {STRINGS.book.review.editorCancelButton}
        </button>
        <button
          type="button"
          onClick={handleSave}
          // Position-1 Commit-2 post-Q1 (Sebastian Ruling A 2026-09-04) —
          // block over-cap saves client-side. Red counter tells the
          // customer what he needs to know; a round-trip rejection for
          // something visible on screen is noise. Server check stays
          // as the real gate.
          //
          // Position-1 Commit-3 addendum (Sebastian ruling 2026-09-03
          // late-evening) — extend the client gate SYMMETRICALLY to
          // below-min. Pre-slice: save enabled below min, customer
          // clicked, waited for a round-trip, saw "Dein Text ist zu
          // kurz". Same friction the over-cap disable was designed to
          // remove. Now the button carries the below-min signal so
          // the counter can stay neutral there (typing-through-
          // shortness stays uncoloured per Block 2 c4).
          disabled={
            edit.saving ||
            text === initialText ||
            text.length < CUSTOMER_EDIT_MIN_CHARS ||
            text.length > outerCharCeiling
          }
          className="h-10 rounded-lg bg-dream-navy px-4 text-sm font-semibold text-dream-white hover:bg-dream-navy/90 disabled:opacity-40"
        >
          {STRINGS.book.review.editorSaveButton}
        </button>
      </div>
    </div>
  );
}

/** Cover-regen slice — caps sourced from the DTO exports via
 *  `constants/text-limits.ts` (fix/text-field-limits, Sebastian
 *  ruling 2026-09-29). Server re-checks both on every request. */

interface CoverRegenEditorProps {
  readonly bookOrderId: string;
  readonly initialTitle: string;
  readonly renderState: CustomerRenderState;
  readonly regen: PreviewCoverRegen;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}

/**
 * Cover-regen slice (Sebastian ruling 2026-09-05) — the two-field
 * editor. Opens from cover option 1 on the Freigabe screen's Change
 * dialog. Title starts pre-filled with the current book title;
 * comment starts empty. At least one field must be non-empty after
 * trim (server enforces `cover_regen_nothing_to_change` as
 * defense-in-depth; the Save button also disables on both-empty).
 *
 * Cap = `BOOK_CONFIG.customer_cover_regen_cap = 3`. `regen.used`
 * and `regen.remaining` come from the envelope. Save is disabled
 * on `regen.remaining === 0` and shows the frozen
 * `coverRegenCapReachedMessage`.
 *
 * Save is ALSO disabled when `renderState === 'rendering'` — the
 * scene-side lock (Sebastian 2026-09-04 "Warte kurz, dein Bild
 * wird noch gezeichnet.") applies to cover per Ruling 1
 * 2026-09-05.
 *
 * Character counters — title 0/40 with a hard input `maxLength=40`
 * so the title-too-long inline error can never fire in the UI
 * under normal use; comment 0/200 mirrors scene-regen. Same
 * CharacterCounter component both fields use so the visual style
 * is one thing.
 */
function CoverRegenEditor({
  bookOrderId,
  initialTitle,
  renderState,
  regen,
  onClose,
  onSaved,
}: CoverRegenEditorProps) {
  const [title, setTitle] = useState(initialTitle);
  const [comment, setComment] = useState('');
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const titleTrimmed = title.trim();
  const commentTrimmed = comment.trim();
  const bothEmpty = titleTrimmed.length === 0 && commentTrimmed.length === 0;
  const capReached = regen.remaining === 0;
  const isRegenPending = renderState === 'rendering';

  const handleSave = useCallback(async () => {
    setSaveErrorMessage(null);
    setSaving(true);
    try {
      const body: { title?: string; comment?: string } = {};
      if (titleTrimmed.length > 0) body.title = titleTrimmed;
      if (commentTrimmed.length > 0) body.comment = commentTrimmed;
      await regenCover(bookOrderId, body);
      onSaved();
      onClose();
    } catch (err) {
      // Prefer the server's DreamableError envelope's `user_message`
      // (e.g. cover_regen_nothing_to_change, cover_regen_cap_reached,
      // title_too_long, cover_regen_not_in_customer_review). Fall
      // back to the generic string only when nothing is on the wire
      // (network failure).
      const envelopeMessage = err instanceof ApiError ? (err.envelope?.user_message ?? null) : null;
      setSaveErrorMessage(envelopeMessage ?? STRINGS.book.review.editorSaveError);
      setSaving(false);
    }
  }, [bookOrderId, titleTrimmed, commentTrimmed, onSaved, onClose]);

  return (
    <div className="space-y-3">
      <h2 id="page-change-dialog-heading" className="text-lg font-semibold text-dream-navy pr-6">
        {STRINGS.book.review.coverRegenDialogHeading}
      </h2>

      {/* Title field — 0/40, hard-stops at 40 via input maxLength.
          `coverRegenTitleTooLongError` is defense-in-depth server-only
          and cannot surface here under normal use. */}
      <div className="space-y-1.5">
        <label htmlFor="cover-regen-title" className="block text-sm text-dream-navy">
          {STRINGS.book.review.coverRegenTitleFieldLabel}
        </label>
        <input
          id="cover-regen-title"
          type="text"
          className="builder-field w-full px-3 py-2 text-base text-dream-navy outline-none"
          value={title}
          maxLength={COVER_TITLE_MAX_CHARS}
          onChange={(e) => {
            setTitle(e.target.value);
            if (saveErrorMessage !== null) setSaveErrorMessage(null);
          }}
          aria-label={STRINGS.book.review.coverEditorAriaLabel}
          autoFocus
        />
        <CharacterCounter
          value={titleTrimmed.length}
          corridor={{ min: 0, max: COVER_TITLE_MAX_CHARS }}
        />
        <p className="text-xs text-dream-navy/70">{STRINGS.book.review.coverRegenTitleFieldHint}</p>
      </div>

      {/* Comment field — 0/200. No lower bound per Sebastian ruling
          2026-09-05: "'Mehr Sonne' is a legitimate wish." */}
      <div className="space-y-1.5">
        <label htmlFor="cover-regen-comment" className="block text-sm text-dream-navy">
          {STRINGS.book.review.coverRegenCommentFieldLabel}
        </label>
        <textarea
          id="cover-regen-comment"
          className="builder-field w-full px-3 py-2 text-base text-dream-navy outline-none resize-none"
          rows={3}
          value={comment}
          maxLength={COVER_COMMENT_MAX_CHARS}
          onChange={(e) => {
            setComment(e.target.value);
            if (saveErrorMessage !== null) setSaveErrorMessage(null);
          }}
        />
        <CharacterCounter
          value={commentTrimmed.length}
          corridor={{ min: 0, max: COVER_COMMENT_MAX_CHARS }}
        />
        <p className="text-xs text-dream-navy/70">
          {STRINGS.book.review.coverRegenCommentFieldHint}
        </p>
      </div>

      {/* Cap-reached inline message — replaces the counter/hint pair
          when regen.remaining === 0. Wording is the frozen key 8
          from the 2026-09-05 release and references "unten das
          Motiv aus" — that surface is the pick-earlier slice
          shipping before 2026-09-15 (tracker
          [cover-regen-cap-message-ahead-of-picker]). */}
      {capReached && (
        <p className="text-sm text-dream-navy/70">
          {STRINGS.book.review.coverRegenCapReachedMessage}
        </p>
      )}

      {/* Remaining-uses hint — shown only after the first re-render.
          Sebastian rule: "a limit is only named once one has been
          spent." `regen.used >= 1 && regen.remaining >= 1`. */}
      {!capReached && regen.used >= 1 && (
        <p className="text-xs text-dream-navy/70">{STRINGS.book.review.coverRegenRemainingHint}</p>
      )}

      {/* Both-empty inline error — surfaces before the request fires
          if the customer clicks Save with both fields empty. The
          Save button is also disabled in that state, so this only
          appears when a paste-then-clear leaves both fields empty
          post-error. */}
      {saveErrorMessage !== null && (
        <p role="alert" className="text-sm text-error">
          {saveErrorMessage}
        </p>
      )}

      <div className="flex gap-3 justify-end pt-1">
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="h-10 rounded-lg border border-dream-navy/30 px-4 text-sm font-semibold text-dream-navy hover:bg-dream-navy/5 disabled:opacity-40"
        >
          {STRINGS.book.review.editorCancelButton}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || bothEmpty || capReached || isRegenPending}
          className="h-10 rounded-lg bg-dream-navy px-4 text-sm font-semibold text-dream-white hover:bg-dream-navy/90 disabled:opacity-40"
        >
          {STRINGS.book.review.editorSaveButton}
        </button>
      </div>
    </div>
  );
}
